/**
 * Every endpoint the console uses, named once.
 *
 * Paths are built here and nowhere else. A path assembled at a call site is
 * how a channel id ends up missing from one query and not another — the
 * single-scoping-module rule (L15) seen from the client, where the console is
 * multi-tenant too and a request that forgets which channel it is for is the
 * same bug from the other side.
 *
 * The schemas are the PUBLISHED ones from @tomo/contracts, the same objects
 * the OpenAPI document is generated from. A hand-written interface here would
 * be a second definition free to drift.
 */
import { z } from 'zod';
import {
  Asset, AssetUpload, Bundle, Created, Instance, Item, ItemBuildPreview, LoginStarted, Ok,
  RarityTier, Reward, Session, Trade, Trigger, pageOf,
  type ItemTemplate,
  type BundleInput, type RewardInput, type TriggerInput,
  BundleCapacity, SimulationResult,
  type SimulateRequest,
  BugReport, type BugReportInput,
  Room, RoomRoster, RoomGroupings, TwitchCategoryList,
  type RoomGame, type RoomGroupingsInput,
  RoomAdmin,
  RoomMute,
} from '@tomo/contracts';
import { ApiError, request, requestAll } from './api.js';

const channelPath = (channelId: string) => `/v1/channels/${encodeURIComponent(channelId)}`;

export const auth = {
  /** A 401 here is the ordinary "not signed in", not a failure to surface. */
  session: (signal?: AbortSignal) =>
    request('/v1/auth/session', Session, signal ? { signal } : {}),

  async start(returnTo: string): Promise<string> {
    const started = await request('/v1/auth/start', LoginStarted, {
      method: 'POST',
      body: { return_to: returnTo },
    });
    return started.authorize_url;
  },

  signOut: () => request('/v1/auth/session', Ok, { method: 'DELETE' }),
};

/**
 * One shape per catalog resource, so three list screens are one component.
 *
 * `update` takes a PARTIAL: the server merges it onto the current resource and
 * revalidates the whole thing, so sending only what changed is both correct
 * and the smaller request.
 */
export interface Catalog<W, I> {
  list: (channelId: string, signal?: AbortSignal) => Promise<W[]>;
  get: (channelId: string, id: string, signal?: AbortSignal) => Promise<W>;
  create: (channelId: string, input: I) => Promise<{ id: string }>;
  update: (channelId: string, id: string, patch: Partial<I>) => Promise<W>;
}

function catalog<W, I>(segment: string, schema: z.ZodType<W>): Catalog<W, I> {
  const collection = (channelId: string) => `${channelPath(channelId)}/${segment}`;
  const item = (channelId: string, id: string) =>
    `${collection(channelId)}/${encodeURIComponent(id)}`;

  return {
    list: (channelId, signal) => requestAll(collection(channelId), pageOf(schema), signal),
    get: (channelId, id, signal) => request(item(channelId, id), schema, signal ? { signal } : {}),
    create: (channelId, input) =>
      request(collection(channelId), Created, { method: 'POST', body: input }),
    update: (channelId, id, patch) =>
      request(item(channelId, id), schema, { method: 'PATCH', body: patch }),
  };
}

export const rewards = catalog<Reward, RewardInput>('rewards', Reward);
export const bundles = catalog<Bundle, BundleInput>('bundles', Bundle);
export const triggers = catalog<Trigger, TriggerInput>('triggers', Trigger);

/**
 * Named so a screen can say "rewards" without knowing the path.
 *
 * `reads` is the scope the section needs. Triggers take the AUTHORING scope
 * rather than the viewer-facing `rewards:read`, because a trigger is the
 * payout rule and listing them tells a viewer exactly what to do to be paid.
 * The nav uses this so a section nobody can open is not offered — a tab that
 * always 403s is worse than no tab.
 */
/**
 * What a bundle can do right now, and what it would actually hand out.
 *
 * Both computed server-side by the same selector that mints — `apps` may not
 * import `domain`, and that constraint turns out to be the right answer
 * anyway: running it here would be a second implementation of the draw, and a
 * preview that can disagree with production is the thing this replaces.
 */
export const packs = {
  capacity: (channelId: string, bundleId: string, signal?: AbortSignal) =>
    request(
      `${channelPath(channelId)}/bundles/${encodeURIComponent(bundleId)}/capacity`,
      BundleCapacity,
      signal ? { signal } : {}
    ),
  simulate: (channelId: string, bundleId: string, body: SimulateRequest) =>
    request(
      `${channelPath(channelId)}/bundles/${encodeURIComponent(bundleId)}/simulate`,
      SimulationResult,
      { method: 'POST', body }
    ),
};

export const CATALOG = {
  rewards: { label: 'Rewards', reads: 'rewards:read' },
  bundles: { label: 'Bundles', reads: 'rewards:read' },
  triggers: { label: 'Triggers', reads: 'rewards:write' },
} as const;

export type CatalogKind = keyof typeof CATALOG;
export const CATALOG_KINDS = Object.keys(CATALOG) as CatalogKind[];

/**
 * Minted copies.
 *
 * `mine` is the one route that is not channel-scoped, because a collection is
 * the person's and spans every channel they belong to.
 */
export const instances = {
  mine: (channelId: string | undefined, signal?: AbortSignal) =>
    requestAll(
      channelId
        ? `/v1/me/instances?channel=${encodeURIComponent(channelId)}`
        : '/v1/me/instances',
      pageOf(Instance),
      signal
    ),

  forChannel: (channelId: string, signal?: AbortSignal) =>
    requestAll(`${channelPath(channelId)}/instances`, pageOf(Instance), signal),

  /**
   * Opening one. Idempotent by design — a second click answers with the same
   * item, revealed, rather than an error (L29).
   */
  reveal: (channelId: string, id: string) =>
    request(
      `${channelPath(channelId)}/instances/${encodeURIComponent(id)}/reveal`,
      Instance,
      { method: 'POST' }
    ),
};

/**
 * Trades.
 *
 * Not under a channel: a trade is the cross-tenant object by definition.
 * `confirm` sends the HASH you looked at, and the server refuses it if the
 * deal has moved on since — which is what makes a last-second swap visible
 * rather than silent.
 */
/**
 * Reports people have filed. Platform admins only; the API refuses the rest.
 *
 * Read-only from here on purpose. There is no triage state, no assignee and
 * no resolve button, because the first version of a bug tracker that nobody
 * uses is the one with a workflow. This is a list you read.
 */
/**
 * A community, as its public homepage reads it.
 *
 * Both calls are unauthenticated on purpose. This is the page somebody opens
 * from a link in a Discord, and a front door that asks who you are before
 * telling you whether anybody is home is not a front door.
 *
 * NO NEW ENDPOINT. The room route already takes a slug and the roster route
 * already returns tags, descriptions and live detail for a manual roster —
 * a third way to ask the same question would be a third thing to keep in
 * step with the other two.
 */
export const communities = {
  get: (slug: string, signal?: AbortSignal) =>
    request(`/v1/rooms/${encodeURIComponent(slug)}`, Room, signal ? { signal } : {}),

  roster: (slug: string, signal?: AbortSignal) =>
    request(
      `/v1/rooms/${encodeURIComponent(slug)}/roster`,
      RoomRoster,
      signal ? { signal } : {},
    ),
};

/**
 * Running a community: who may edit it, who is blocked, and who is in it.
 *
 * Owner-only on the server for `admins`, moderator-level for the rest. The
 * console does not re-implement those rules — it asks, and shows what comes
 * back. A second copy of an authority check is a second thing to get wrong,
 * and the one that matters is the server's.
 */
export const communityAdmin = {
  // `request`, not `requestAll`: these answer with a plain list and no
  // cursor. A community has a handful of admins, not a page of them.
  list: (slug: string, signal?: AbortSignal) =>
    request(
      `/v1/rooms/${encodeURIComponent(slug)}/admins`,
      z.object({ data: z.array(RoomAdmin) }),
      signal ? { signal } : {},
    ).then((r) => r.data),

  add: (slug: string, handle: string, role: 'owner' | 'editor') =>
    request(`/v1/rooms/${encodeURIComponent(slug)}/admins`, RoomAdmin, {
      method: 'PUT', body: { handle, role },
    }),

  /**
   * Remove one row: a member by uid, a waiting grant by Twitch user id. Never
   * by handle — a handle is whoever holds that name now.
   */
  remove: (slug: string, row: { uid: string | null; twitch_user_id: string | null }) =>
    request(
      row.uid !== null
        ? `/v1/rooms/${encodeURIComponent(slug)}/members/${encodeURIComponent(row.uid)}`
        : `/v1/rooms/${encodeURIComponent(slug)}/invitations/${encodeURIComponent(row.twitch_user_id ?? '')}`,
      Ok,
      { method: 'DELETE' },
    ),

  /** Everyone silenced here. A block is one with a date nobody will reach. */
  blocks: (slug: string, signal?: AbortSignal) =>
    request(
      `/v1/rooms/${encodeURIComponent(slug)}/mutes`,
      z.object({ data: z.array(RoomMute) }),
      signal ? { signal } : {},
    ).then((r) => r.data),

  unblock: (slug: string, uid: string) =>
    request(
      `/v1/rooms/${encodeURIComponent(slug)}/mutes/${encodeURIComponent(uid)}`,
      Ok,
      { method: 'DELETE' },
    ),

  /** The community's own list of channels. Replaces it wholesale, never merges. */
  /**
   * What the community page says under its headline. An empty string clears
   * it: the page shows nothing for an empty description, so there is no need
   * for a separate "remove" that the route would have to learn.
   */
  setDescription: (slug: string, description: string) =>
    request(`/v1/rooms/${encodeURIComponent(slug)}`, Room, {
      method: 'PATCH', body: { description },
    }),

  /** Listed on the front page, or reachable only by its link. */
  setVisibility: (slug: string, visibility: 'public' | 'private') =>
    request(`/v1/rooms/${encodeURIComponent(slug)}`, Room, {
      method: 'PATCH', body: { visibility },
    }),

  setChannels: (slug: string, logins: string[]) =>
    request(`/v1/rooms/${encodeURIComponent(slug)}`, Room, {
      method: 'PATCH', body: { twitch_logins: logins },
    }),

  /**
   * Take the list over from a feed: copy it, and stop reading theirs.
   *
   * ONE PATCH, and that is the point rather than a convenience. The server
   * will not copy on your behalf — a borrowed list quietly becoming an owned
   * one is not something to do behind somebody's back — so the copy and the
   * detach are one intention that has to travel together. Sent as two calls
   * there is a window where the community is empty, and a failure between
   * them leaves it that way.
   */
  /** Everyone's groupings, with who set each (ADR 0052). Editors only. */
  groupings: (slug: string, signal?: AbortSignal) =>
    request(
      `/v1/rooms/${encodeURIComponent(slug)}/groupings`,
      RoomGroupings,
      signal ? { signal } : {},
    ),

  /**
   * The definitions and the editors' ticks, whole. What a streamer set for
   * themselves is kept by the server whatever this says.
   */
  saveGroupings: (slug: string, body: RoomGroupingsInput) =>
    request(`/v1/rooms/${encodeURIComponent(slug)}/groupings`, RoomGroupings, {
      method: 'PUT', body,
    }),

  /** The Twitch categories this community is about. Empty clears them. */
  setGames: (slug: string, games: RoomGame[]) =>
    request(`/v1/rooms/${encodeURIComponent(slug)}`, Room, {
      method: 'PATCH', body: { games },
    }),

  /** Twitch's own list of games, searched on the server with the app token. */
  searchGames: (query: string, signal?: AbortSignal) =>
    request(
      `/v1/twitch/categories?q=${encodeURIComponent(query)}`,
      TwitchCategoryList,
      signal ? { signal } : {},
    ).then((r) => r.data),

  takeOverList: (slug: string, logins: string[]) =>
    request(`/v1/rooms/${encodeURIComponent(slug)}`, Room, {
      method: 'PATCH', body: { twitch_logins: logins, roster_feed: null },
    }),
};

export const bugReports = {
  list: (signal?: AbortSignal) => requestAll('/v1/bug-reports', pageOf(BugReport), signal),

  /** Open to everyone: a signed-out reporter is still heard. */
  send: (input: z.input<typeof BugReportInput>) =>
    request('/v1/bug-reports', Created, { method: 'POST', body: input }),
};

/**
 * Who YOU have blocked, everywhere — not a community's mutes, which are
 * `communityAdmin.blocks`. A block is the one moderation action a viewer
 * takes themselves, and a list you cannot review is a setting you cannot undo.
 */
export const myBlocks = {
  list: (signal?: AbortSignal) =>
    request(
      '/v1/blocks',
      z.object({ data: z.array(z.object({ uid: z.string(), login: z.string().nullable() })) }),
      signal ? { signal } : {},
    ).then((r) => r.data),

  unblock: (uid: string) =>
    request(`/v1/blocks/${encodeURIComponent(uid)}`, z.object({}), { method: 'DELETE' }),
};

export const trades = {
  mine: (signal?: AbortSignal) => requestAll('/v1/trades', pageOf(Trade), signal),

  get: (id: string, signal?: AbortSignal) =>
    request(`/v1/trades/${encodeURIComponent(id)}`, Trade, signal ? { signal } : {}),

  open: (participants: string[]) =>
    request('/v1/trades', Trade, { method: 'POST', body: { participants } }),

  /** Your WHOLE side, never a partial merge. */
  setOffers: (id: string, offers: { instance_id: string; to_uid: string }[]) =>
    request(`/v1/trades/${encodeURIComponent(id)}/offers`, Trade, {
      method: 'PUT', body: { offers },
    }),

  confirm: (id: string, offerHash: string) =>
    request(`/v1/trades/${encodeURIComponent(id)}/confirm`, Trade, {
      method: 'POST', body: { offer_hash: offerHash },
    }),

  cancel: (id: string) =>
    request(`/v1/trades/${encodeURIComponent(id)}/cancel`, Trade, { method: 'POST' }),
};

/**
 * The channel's rarity ladder.
 *
 * Read by anything that renders a tier. Retired tiers come back by default,
 * because a copy stamped with one still has to show its colour — the console
 * must never be the reason a collectible loses its name.
 */
export const rarity = {
  list: (channelId: string, signal?: AbortSignal) =>
    request(
      `${channelPath(channelId)}/rarity-tiers`,
      z.object({ data: z.array(RarityTier) }),
      signal ? { signal } : {}
    ).then((r) => r.data),

  put: (channelId: string, tier: Omit<RarityTier, 'channel_id' | 'retired'>) =>
    request(
      `${channelPath(channelId)}/rarity-tiers/${encodeURIComponent(tier.key)}`,
      RarityTier,
      { method: 'PUT', body: tier }
    ),

  /** Retire, never delete. A copy stamped with a tier must resolve forever. */
  retire: (channelId: string, key: string, retired: boolean) =>
    request(
      `${channelPath(channelId)}/rarity-tiers/${encodeURIComponent(key)}/retire`,
      RarityTier,
      { method: 'POST', body: { retired } }
    ),
};

/**
 * Files.
 *
 * Upload is three steps and the middle one does not touch tomo at all: the
 * bytes go straight from the browser to the bucket with a signed URL, so a
 * 25MB image is never a Cloud Run memory problem. The last call is where the
 * file is actually identified — by its bytes, not by anything said about it.
 */
export const assets = {
  list: (channelId: string, signal?: AbortSignal) =>
    request(
      `${channelPath(channelId)}/assets`,
      z.object({ data: z.array(Asset) }),
      signal ? { signal } : {}
    ).then((r) => r.data),

  async upload(
    channelId: string,
    file: File,
    tags: string[],
    onProgress?: (fraction: number) => void
  ): Promise<Asset> {
    const reserved = await request(`${channelPath(channelId)}/assets`, AssetUpload, {
      method: 'POST',
      body: {
        name: file.name, content_type: file.type, bytes: file.size, tags,
      },
    });

    // Straight to the bucket. `credentials` is deliberately omitted — sending
    // the session cookie to storage.googleapis.com would be handing it to a
    // third party, and the signed URL is the only authorisation needed.
    const put = await fetch(reserved.upload_url, {
      method: 'PUT',
      headers: { 'content-type': file.type },
      body: file,
    });
    if (!put.ok) {
      throw new ApiError(
        'internal', put.status,
        'The file could not be uploaded. Check your connection and try again.'
      );
    }
    onProgress?.(1);

    return request(
      `${channelPath(channelId)}/assets/${encodeURIComponent(reserved.asset.id)}/complete`,
      Asset,
      { method: 'POST' }
    );
  },

  patch: (channelId: string, id: string, patch: { name?: string; tags?: string[] }) =>
    request(`${channelPath(channelId)}/assets/${encodeURIComponent(id)}`, Asset, {
      method: 'PATCH', body: patch,
    }),

  retire: (channelId: string, id: string, retired: boolean) =>
    request(`${channelPath(channelId)}/assets/${encodeURIComponent(id)}/retire`, Asset, {
      method: 'POST', body: { retired },
    }),
};

/**
 * Collectibles.
 *
 * `preview` writes nothing and `build` is all-or-nothing, which is why they
 * are two calls: the only way to learn what a build would do is to ask, and
 * the only safe answer to a bad batch is to create none of it.
 */
export const items = {
  list: (channelId: string, signal?: AbortSignal) =>
    request(
      `${channelPath(channelId)}/items`,
      z.object({ data: z.array(Item) }),
      signal ? { signal } : {}
    ).then((r) => r.data),

  preview: (channelId: string, assetIds: string[], template: ItemTemplate) =>
    request(`${channelPath(channelId)}/items/preview`, ItemBuildPreview, {
      method: 'POST', body: { asset_ids: assetIds, template },
    }),

  build: (channelId: string, assetIds: string[], template: ItemTemplate) =>
    request(
      `${channelPath(channelId)}/items/build`,
      z.object({ data: z.array(Item) }),
      { method: 'POST', body: { asset_ids: assetIds, template } }
    ).then((r) => r.data),

  patch: (channelId: string, id: string, patch: Partial<ItemTemplate> & { name?: string; tags?: string[] }) =>
    request(`${channelPath(channelId)}/items/${encodeURIComponent(id)}`, Item, {
      method: 'PATCH', body: patch,
    }),

  retire: (channelId: string, id: string, retired: boolean) =>
    request(`${channelPath(channelId)}/items/${encodeURIComponent(id)}/retire`, Item, {
      method: 'POST', body: { retired },
    }),
};
